import React, { createContext, useContext, useState, useEffect, useCallback, useRef } from 'react';

// Core translations - only essential keys for app shell
// Other translations are co-located with their components and registered dynamically
const coreTranslations = {
  en: {
    "app.title": "Immobilien-Investment-Analyzer",
    "app.subtitle": "German Real Estate Investment Analysis",
    "app.inputParameters": "Input Parameters",
    "app.financialProjections": "Financial Projections",
    "accordion.purchaseFinancing": "Purchase & Financing",
    "accordion.startupCosts": "Acquisition Costs (Kaufnebenkosten)",
    "accordion.fixedRecurring": "Fixed Recurring Costs",
    "accordion.variableRecurring": "Variable Recurring Costs",
    "accordion.scheduledCapex": "Scheduled Capital Expenditures",
    "accordion.emergencyReserve": "Emergency Reserve Fund",
    "accordion.depreciation": "Depreciation & Asset Value",
    "common.years": "years",
    "common.loading": "Loading...",
    "common.addItem": "+ Add Item",
    "common.costName": "Cost name",
    "common.lowerBound": "Lower Bound",
    "common.upperBound": "Upper Bound",
    "common.fixedPerOccurrence": "€ (fixed per occurrence)",
    "common.percentOfRevenue": "% (of revenue)",
    "tabs.pl": "Profit & Loss",
    "tabs.bs": "Balance Sheet",
    "tabs.both": "Side-by-Side",
    "tabs.exportPdf": "Export PDF",
    "app.disclaimer": "For entertainment purposes only. Not investment advice.",
    "app.banner": "For research and educational purposes only \u2014 not investment, tax, or legal advice. Consult a qualified Steuerberater.",
  },
  de: {
    "app.title": "Immobilien-Investment-Analyzer",
    "app.subtitle": "Immobilien-Investitionsanalyse f\u00fcr Deutschland",
    "app.inputParameters": "Eingabeparameter",
    "app.financialProjections": "Finanzprognosen",
    "accordion.purchaseFinancing": "Kauf & Finanzierung",
    "accordion.startupCosts": "Kaufnebenkosten (Jahr 0)",
    "accordion.fixedRecurring": "Feste wiederkehrende Kosten",
    "accordion.variableRecurring": "Variable wiederkehrende Kosten",
    "accordion.scheduledCapex": "Geplante Investitionen",
    "accordion.emergencyReserve": "Notfallreserve",
    "accordion.depreciation": "Abschreibung & Vermögenswert",
    "common.years": "Jahre",
    "common.loading": "Wird geladen...",
    "common.addItem": "+ Hinzufügen",
    "common.costName": "Kostenbezeichnung",
    "common.lowerBound": "Untergrenze",
    "common.upperBound": "Obergrenze",
    "common.fixedPerOccurrence": "€ (Fixbetrag pro Vorgang)",
    "common.percentOfRevenue": "% (vom Umsatz)",
    "tabs.pl": "Gewinn & Verlust",
    "tabs.bs": "Bilanz",
    "tabs.both": "Nebeneinander",
    "tabs.exportPdf": "PDF Export",
    "app.disclaimer": "Nur zur Unterhaltung. Keine Anlageberatung.",
    "app.banner": "Ausschlie\u00dflich zu Recherche- und Bildungszwecken \u2014 keine Anlage-, Steuer- oder Rechtsberatung. Konsultieren Sie einen qualifizierten Steuerberater.",
  }
};

const LanguageContext = createContext();

// Detect browser language, default to English
function getDefaultLanguage() {
  const stored = localStorage.getItem('immobilienAnalyzer_language');
  if (stored && (stored === 'en' || stored === 'de')) {
    return stored;
  }

  const browserLang = navigator.language?.split('-')[0];
  if (browserLang === 'de') {
    return 'de';
  }

  return 'en';
}

export function LanguageProvider({ children }) {
  const [language, setLanguageState] = useState(getDefaultLanguage);
  const [, forceUpdate] = useState(0);

  // Store all registered translations
  const translationsRef = useRef({
    en: { ...coreTranslations.en },
    de: { ...coreTranslations.de }
  });

  const setLanguage = useCallback((lang) => {
    if (lang === 'en' || lang === 'de') {
      setLanguageState(lang);
      localStorage.setItem('immobilienAnalyzer_language', lang);
    }
  }, []);

  // Register additional translations (called by lazy-loaded components)
  const registerTranslations = useCallback((newTranslations) => {
    let hasNew = false;

    ['en', 'de'].forEach(lang => {
      if (newTranslations[lang]) {
        const current = translationsRef.current[lang];
        const incoming = newTranslations[lang];

        // Check if we have any new keys
        for (const key of Object.keys(incoming)) {
          if (!(key in current)) {
            hasNew = true;
            current[key] = incoming[key];
          }
        }
      }
    });

    // Only trigger re-render if we added new translations
    if (hasNew) {
      forceUpdate(n => n + 1);
    }
  }, []);

  // Persist on mount if not already stored
  useEffect(() => {
    if (!localStorage.getItem('immobilienAnalyzer_language')) {
      localStorage.setItem('immobilienAnalyzer_language', language);
    }
  }, [language]);

  // Translation function with fallback to key, supports {placeholder} interpolation
  const t = useCallback((key, params) => {
    const translations = translationsRef.current;
    let text = translations[language]?.[key] || translations.en?.[key] || key;
    if (params) {
      Object.entries(params).forEach(([k, v]) => {
        text = text.replace(new RegExp(`\\{${k}\\}`, 'g'), String(v));
      });
    }
    return text;
  }, [language]);

  const value = {
    language,
    setLanguage,
    t,
    registerTranslations,
    availableLanguages: ['en', 'de'],
  };

  return (
    <LanguageContext.Provider value={value}>
      {children}
    </LanguageContext.Provider>
  );
}

export function useLanguage() {
  const context = useContext(LanguageContext);
  if (!context) {
    throw new Error('useLanguage must be used within a LanguageProvider');
  }
  return context;
}

export default LanguageContext;
